import { useState } from 'react'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'

import { GetApiErrorMessage, SaveFile } from 'helpers'
import { GenerateSmartPayExportCsvFromInputFile as GenerateSmartPayExportCsvFromInputFileApi } from 'services/RestClient'
import { ExportSmartPayCsvFromInputFileResult } from 'types'

import { FileDropzone } from './ui'
import { ExportActions } from './ui/ExportActions'

interface IProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const UploadSmartPayInputFile = ({ open, onClose, onSuccess }: IProps) => {
  const [generateExport, setGenerateExport] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [exportResult, setExportResult] = useState<ExportSmartPayCsvFromInputFileResult | null>(
    null,
  )

  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only CSV files are allowed')
      return
    }

    setSelectedFile(file)
    toast.success(`File "${file.name}" selected.`)
  }

  const handleGenerateExport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first')
      return
    }

    setGenerateExport(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await GenerateSmartPayExportCsvFromInputFileApi(formData)

      if (response.data) {
        setExportResult(response.data)
        toast.success(`Export completed. You can now download the export CSV.`)
        onSuccess?.()
      }
    } catch (error: any) {
      console.error('Generate export error:', error)
      console.error('Error response data:', error?.response?.data)

      const errorMessage = GetApiErrorMessage(error)
      toast.error(errorMessage)
    } finally {
      setGenerateExport(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setGenerateExport(false)
    setExportResult(null)
    onClose()
  }

  const downloadOutputCsv = () => {
    if (!exportResult?.exportCsv) return

    const fileName = `SmartPay_Export_From_${selectedFile?.name || 'Unknown_File'}_${
      new Date().toISOString().split('T')[0]
    }.csv`
    SaveFile(exportResult.exportCsv, fileName)
  }

  const downloadNotExportedCsv = () => {
    if (!exportResult?.notExportedCsv) return

    const fileName = `SmartPay_Not_Exported_From_${selectedFile?.name || 'Unknown_File'}_${
      new Date().toISOString().split('T')[0]
    }.csv`
    SaveFile(exportResult.notExportedCsv, fileName)
  }

  const handleUploadNewFile = () => {
    setSelectedFile(null)
    setExportResult(null)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        Generate SmartPay Export CSV from Input File
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {generateExport && <LinearProgress sx={{ mb: 2 }} />}
        {!exportResult && (
          <Typography variant='body1' sx={{ mb: 2 }}>
            Upload the SmartPay CSV input file you received. The system will generate a new CSV file
            formatted for SmartPay export based on the records found in the input file matching
            those with archived APRs. After processing, you can download the generated SmartPay
            export CSV and use it for your SmartPay operations. Additionally, if there are any records
            that could not be exported due to validation errors, duplication, or not being found, a separate CSV
            will be available for download containing those records.
          </Typography>
        )}

        {exportResult ? (
          <div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
              <Typography
                variant='h6'
                sx={{ mb: 1, ml: 3, display: 'flex', justifyContent: 'flex-start' }}>
                SmartPay Export CSV is ready!
              </Typography>
              <CheckCircleIcon color='success' sx={{ fontSize: 32 }} />
            </div>
            <Typography variant='h6' component='div'>
              <strong>Export Summary:</strong>
              <ul>
                <li>{`Total records: ${exportResult.totalRecords}`}</li>
                <li>{`Exported records: ${exportResult.exportedCount}`}</li>
                <li>{`Not exported records (invalid, duplicated or not found): ${exportResult.notExportedCount}`}</li>
              </ul>
            </Typography>
            <ExportActions
              result={exportResult}
              onDownloadExportCsv={downloadOutputCsv}
              onDownloadNotExportedCsv={downloadNotExportedCsv}
              onUploadNewFile={handleUploadNewFile}
            />
          </div>
        ) : (
          <FileDropzone
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            disabled={generateExport}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color='inherit'>
          Close
        </Button>
        {!exportResult && (
          <Button
            onClick={handleGenerateExport}
            variant='contained'
            color='primary'
            disabled={!selectedFile || generateExport}>
            {generateExport ? 'Generating export...' : 'Generate SmartPay Export'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
